import React, { useState } from 'react';
import styled from 'styled-components';
import { FaArrowRight, FaCheck, FaEnvelope, FaUser } from 'react-icons/fa';
import { Input } from '../../../components/ui/Input/Input';
import { Select } from '../../../components/ui/Select/Select';
import { Button } from '../../../components/ui/Button/Button';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { formatCPFCNPJ, unformat } from '../../../utils/formatters';
import { validateDocument, validateEmail } from '../../../utils/validators';
import { resetSenhaService } from '../services/passwordService';
import logger from '../../../utils/logger';
import { destacarPendencias } from '../../../utils/destaqueCampos';

// Padrão do EMAIL_RECUPERACAO_MAX_TENTATIVAS da API. Só aparece no aviso
// antes da primeira resposta; depois vale o tentativasRestantes devolvido
const MAX_TENTATIVAS_PADRAO = 3;

// Setor que mantém o cadastro: ajuda quando as respostas não conferem ou o
// cadastro não tem os dados para conferir
export const CONTATO_SSI = 'Para ajuda, entre em contato com o setor de Seleção e Inscrição (SSI) da OAB/MS: (67) 3318-4711 ou (67) 3318-4712.';

const Form = styled.form`
  width: 100%;
`;

const InfoBox = styled.div`
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  font-size: 0.9rem;
  color: ${colors.textMuted};
  line-height: 1.5;

  strong {
    color: ${colors.darkBlue};
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.8rem;
  }
`;

const WarningBox = styled.div`
  background: ${props => props.$critico ? '#fdecea' : '#fff8e1'};
  border-left: 4px solid ${props => props.$critico ? '#dc3545' : '#f0ad4e'};
  border-radius: 8px;
  padding: 1rem;
  margin: 0 0 1.5rem 0;
  font-size: 0.9rem;
  color: #5f4b00;
  line-height: 1.5;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.8rem;
  }
`;

const RESPOSTAS_VAZIAS = {
  dataNascimento: '',
  filiacao: '',
  categoria: '',
  inscricao: '',
  dataCompromisso: '',
  dataRegistro: ''
};

const hoje = () => new Date().toISOString().slice(0, 10);

/**
 * Cadastro/troca do e-mail de recuperação sem login, em 2 etapas:
 * 1. 'identificacao' - CPF/CNPJ + novo e-mail. A API devolve MISSING_FIELDS
 *    com as perguntas do perfil gravado no banco, sem contar tentativa.
 * 2. 'perguntas' - o usuário responde só as perguntas devolvidas. Cada
 *    resposta errada conta tentativa e, no limite, a API desativa o login.
 * `documentoInicial` (sem máscara) vem da tela de recuperação de senha.
 */
export const RecoveryEmailForm = ({ documentoInicial = '', onSuccess, onBlocked, onError }) => {
  const [etapa, setEtapa] = useState('identificacao'); // 'identificacao' | 'perguntas'
  const [documento, setDocumento] = useState(formatCPFCNPJ(documentoInicial));
  const [novoEmail, setNovoEmail] = useState('');
  const [confirmarEmail, setConfirmarEmail] = useState('');
  const [perguntas, setPerguntas] = useState([]);
  const [respostas, setRespostas] = useState(RESPOSTAS_VAZIAS);
  const [tentativasRestantes, setTentativasRestantes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const documentoNumeros = unformat(documento);
  const isCNPJ = documentoNumeros.length === 14;
  // Sociedade só tem uma categoria possível: enviada sem perguntar
  const perguntarCategoria = perguntas.includes('categoria') && !isCNPJ;

  const limparErro = (campo) => {
    if (errors[campo]) setErrors(prev => ({ ...prev, [campo]: undefined }));
  };

  const handleResposta = (campo) => (e) => {
    setRespostas(prev => ({ ...prev, [campo]: e.target.value }));
    limparErro(campo);
  };

  const labelInscricao = () => {
    if (isCNPJ) return 'Número de registro da sociedade';
    if (respostas.categoria === 'EST') return 'Número de inscrição de estagiário(a)';
    return 'Número de inscrição na OAB/MS';
  };

  const validarIdentificacao = () => {
    const novosErros = {};

    if (!documentoNumeros) {
      novosErros.documento = 'Por favor, informe seu CPF ou CNPJ';
    } else if (!validateDocument(documentoNumeros)) {
      novosErros.documento = 'CPF ou CNPJ inválido';
    }

    if (!novoEmail.trim()) {
      novosErros.novoEmail = 'Informe o novo e-mail de recuperação';
    } else if (!validateEmail(novoEmail.trim())) {
      novosErros.novoEmail = 'E-mail inválido';
    }

    if (!confirmarEmail.trim()) {
      novosErros.confirmarEmail = 'Confirme o novo e-mail';
    } else if (novoEmail.trim().toLowerCase() !== confirmarEmail.trim().toLowerCase()) {
      novosErros.confirmarEmail = 'Os e-mails não coincidem';
    }

    setErrors(novosErros);
    return novosErros;
  };

  const validarPerguntas = () => {
    const novosErros = {};

    perguntas.forEach((campo) => {
      if (campo === 'categoria' && !perguntarCategoria) return;
      if (!String(respostas[campo] || '').trim()) {
        novosErros[campo] = 'Campo obrigatório';
      }
    });

    setErrors(novosErros);
    return novosErros;
  };

  const montarRespostas = () => perguntas.reduce((acc, campo) => {
    acc[campo] = campo === 'categoria' && isCNPJ ? 'SOC' : respostas[campo].trim();
    return acc;
  }, {});

  const concluir = async (resultado) => {
    await onSuccess(documentoNumeros, resultado.data);
  };

  const handleIdentificacao = async () => {
    const novosErros = validarIdentificacao();
    if (Object.keys(novosErros).length > 0) {
      destacarPendencias(Object.keys(novosErros));
      return;
    }

    setLoading(true);

    try {
      // Sem respostas, a API só identifica o perfil e devolve as perguntas
      const resultado = await resetSenhaService.trocarEmailRecuperacao(documentoNumeros, novoEmail.trim());
      await concluir(resultado);
    } catch (err) {
      if (err.type === 'missing_fields' && err.missingFields.length > 0) {
        setPerguntas(err.missingFields);
        setRespostas(RESPOSTAS_VAZIAS);
        setErrors({});
        setEtapa('perguntas');
        onError(null);
        return;
      }

      logger.error('Erro ao identificar perfil da troca de e-mail:', err);
      // Cadastro sem dados para conferir é detectado já na etapa 1
      if (err.type === 'recovery_email_change_unavailable') {
        onError({ ...err, message: `${err.message} ${CONTATO_SSI}` });
        return;
      }
      if (err.type === 'invalid_email') {
        setErrors({ novoEmail: err.message });
        destacarPendencias(['novoEmail']);
        return;
      }
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePerguntas = async () => {
    const novosErros = validarPerguntas();
    if (Object.keys(novosErros).length > 0) {
      destacarPendencias(Object.keys(novosErros));
      return;
    }

    setLoading(true);

    try {
      const resultado = await resetSenhaService.trocarEmailRecuperacao(
        documentoNumeros,
        novoEmail.trim(),
        montarRespostas()
      );
      await concluir(resultado);
    } catch (err) {
      logger.error('Erro ao trocar e-mail de recuperação:', err);

      if (err.type === 'account_blocked') {
        onBlocked(err);
        return;
      }

      if (err.type === 'data_mismatch') {
        const restantes = err.tentativasRestantes;
        setTentativasRestantes(restantes);
        onError({
          ...err,
          message: restantes === 1
            ? `${err.message} Atenção: você tem apenas 1 tentativa antes do bloqueio do seu acesso. ${CONTATO_SSI}`
            : `${err.message} Tentativas restantes: ${restantes}. ${CONTATO_SSI}`
        });
        return;
      }

      if (err.type === 'recovery_email_change_unavailable') {
        onError({ ...err, message: `${err.message} ${CONTATO_SSI}` });
        return;
      }

      // O perfil mudou entre as duas etapas: mostra as perguntas certas
      if (err.type === 'missing_fields' && err.missingFields.length > 0) {
        setPerguntas(err.missingFields);
      }
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (etapa === 'identificacao') handleIdentificacao();
    else handlePerguntas();
  };

  const voltarParaIdentificacao = () => {
    setEtapa('identificacao');
    setErrors({});
    onError(null);
  };

  const renderPergunta = (campo) => {
    switch (campo) {
      case 'dataNascimento':
        return (
          <Input
            label="Data de nascimento"
            type="date"
            value={respostas.dataNascimento}
            onChange={handleResposta('dataNascimento')}
            error={errors.dataNascimento}
            max={hoje()}
            required
            disabled={loading}
          />
        );
      case 'filiacao':
        return (
          <Input
            label="Primeiro nome do pai ou da mãe"
            value={respostas.filiacao}
            onChange={handleResposta('filiacao')}
            error={errors.filiacao}
            placeholder="Ex: Maria"
            maxLength={60}
            required
            disabled={loading}
            helper="Como está no seu cadastro na OAB/MS"
          />
        );
      case 'categoria':
        if (!perguntarCategoria) return null;
        return (
          <Select
            label="Categoria da inscrição"
            value={respostas.categoria}
            onChange={handleResposta('categoria')}
            error={errors.categoria}
            required
            disabled={loading}
          >
            <option value="">Selecione</option>
            <option value="ADV">Advogado(a)</option>
            <option value="EST">Estagiário(a)</option>
          </Select>
        );
      case 'inscricao':
        return (
          <Input
            label={labelInscricao()}
            value={respostas.inscricao}
            onChange={handleResposta('inscricao')}
            error={errors.inscricao}
            maxLength={20}
            required
            disabled={loading}
          />
        );
      case 'dataCompromisso':
        return (
          <Input
            label="Data do compromisso"
            type="date"
            value={respostas.dataCompromisso}
            onChange={handleResposta('dataCompromisso')}
            error={errors.dataCompromisso}
            max={hoje()}
            required
            disabled={loading}
          />
        );
      case 'dataRegistro':
        return (
          <Input
            label="Data de registro da sociedade"
            type="date"
            value={respostas.dataRegistro}
            onChange={handleResposta('dataRegistro')}
            error={errors.dataRegistro}
            max={hoje()}
            required
            disabled={loading}
          />
        );
      default:
        return null;
    }
  };

  if (etapa === 'identificacao') {
    return (
      <Form onSubmit={handleSubmit} noValidate>
        <div data-field-name="documento">
          <Input
            label="CPF/CNPJ"
            type="text"
            value={documento}
            onChange={(e) => { setDocumento(formatCPFCNPJ(e.target.value)); limparErro('documento'); }}
            error={errors.documento}
            icon={<FaUser />}
            placeholder="000.000.000-00"
            maxLength={18}
            required
            disabled={loading}
            helper="Digite o CPF ou CNPJ usado no login"
          />
        </div>

        <div data-field-name="novoEmail">
          <Input
            label="Novo e-mail de recuperação"
            type="email"
            value={novoEmail}
            onChange={(e) => { setNovoEmail(e.target.value); limparErro('novoEmail'); }}
            error={errors.novoEmail}
            icon={<FaEnvelope />}
            placeholder="seuemail@exemplo.com"
            maxLength={100}
            required
            disabled={loading}
          />
        </div>

        <div data-field-name="confirmarEmail">
          <Input
            label="Confirmar novo e-mail"
            type="email"
            value={confirmarEmail}
            onChange={(e) => { setConfirmarEmail(e.target.value); limparErro('confirmarEmail'); }}
            onPaste={(e) => e.preventDefault()}
            error={errors.confirmarEmail}
            icon={<FaEnvelope />}
            placeholder="Digite novamente"
            maxLength={100}
            required
            disabled={loading}
          />
        </div>

        <InfoBox>
          <strong>🔒 Como funciona:</strong> para confirmar que é você, faremos
          algumas perguntas sobre o seu cadastro na OAB/MS. Depois disso, o
          código de redefinição de senha passa a ser enviado para o novo e-mail.
        </InfoBox>

        <Button
          type="submit"
          variant="primary"
          size="full"
          disabled={loading}
          rightIcon={<FaArrowRight />}
        >
          {loading ? 'Verificando...' : 'Continuar'}
        </Button>
      </Form>
    );
  }

  const critico = tentativasRestantes === 1;

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <WarningBox $critico={critico}>
        <strong>⚠️ Atenção:</strong>{' '}
        {tentativasRestantes === null
          ? `após ${MAX_TENTATIVAS_PADRAO} respostas incorretas seu acesso será bloqueado e só poderá ser reativado pela OAB/MS.`
          : critico
            ? 'esta é sua última tentativa. Se errar, seu acesso será bloqueado e só poderá ser reativado pela OAB/MS.'
            : `você tem ${tentativasRestantes} tentativas antes do bloqueio do seu acesso.`}
      </WarningBox>

      {perguntas.map((campo) => (
        <div key={campo} data-field-name={campo}>
          {renderPergunta(campo)}
        </div>
      ))}

      <Button
        type="submit"
        variant="primary"
        size="full"
        disabled={loading}
        leftIcon={<FaCheck />}
      >
        {loading ? 'Confirmando...' : 'Confirmar e salvar e-mail'}
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="full"
        onClick={voltarParaIdentificacao}
        disabled={loading}
      >
        Voltar e alterar o e-mail
      </Button>
    </Form>
  );
};
